import { useMemo, useState } from "react";
import { toast } from "sonner";
import { publicApi, formatPublicError, isValidMobile, honeypotOf } from "../../lib/publicApi";
import usePublicSettings from "../../lib/usePublicSettings";

import { tr } from "../../lib/i18n";
import { useAnimatedNumber } from "../../lib/motion";

const AnimatedAmount = ({ value }) => <span data-testid="fin-monthly-value">{Math.round(useAnimatedNumber(value, 400)).toLocaleString()}</span>;
export default function FinancePage() {
  const { settings } = usePublicSettings();
  const enabled = settings?.finance_enabled !== false;
  const rate = Number(settings?.finance_rate_percent ?? 4.5);
  const terms = settings?.finance_terms_months || [24, 36, 48, 60];
  const disclaimer = (settings?.finance_disclaimer && settings.finance_disclaimer !== "Indicative only, not an offer." ? settings.finance_disclaimer : null) || tr("x.indicative_only_not_an_offer");

  const [price, setPrice] = useState(15000);
  const [downPct, setDownPct] = useState(20);
  const [term, setTerm] = useState(48);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const monthly = useMemo(() => {
    const years = term / 12;
    const financed = price * (1 - downPct / 100);
    return Math.max(0, Math.round((financed * (1 + (rate / 100) * years)) / term));
  }, [price, downPct, term, rate]);

  if (!enabled) return (
    <div className="public-container" style={{ padding: "160px 20px", textAlign: "center" }}>
      <h1 style={{ fontSize: 44, fontWeight: 300, letterSpacing: "-0.02em" }}>{tr("x.finance_is_not_currently_offered")}</h1>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidMobile(form.mobile)) { toast.error(tr("x.please_enter_a_valid_mobile_number_7_to_")); return; }
    setSending(true);
    try {
      await publicApi.post("/enquiries", {
        kind: "finance",
        honeypot: honeypotOf(e),
        finance_required: true,
        finance_quote: { price, down_pct: downPct, term_months: term, monthly, rate_percent: rate },
        name: form.name, mobile: form.mobile, email: form.email,
        message: tr("x.finance_request_omr_down_months_omr_mo", { p0: form.message, p1: price, p2: downPct, p3: term, p4: monthly }),
      });
      toast.success(tr("x.thanks_we_ll_call_shortly"));
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) { toast.error(formatPublicError(err)); }
    finally { setSending(false); }
  };

  return (
    <div style={{ paddingTop: 120 }}>
      <section className="pub-finance" style={{ padding: "72px 0 48px" }}>
        <div className="public-container" style={{ maxWidth: 1100 }}>
          <div className="kicker">{tr("x.finance")}</div>
          <h1 style={{ fontSize: "clamp(44px,5.6vw,88px)", fontWeight: 300, letterSpacing: "-0.035em", margin: "0 0 48px" }}>
            {tr("x.pay_monthly")} <em style={{ fontStyle: "normal", fontWeight: 600, color: "var(--p)" }}>{tr("x.pay_smart")}</em>
          </h1>

          <div className="fin-grid">
            <div>
              <div className="fin-row"><label>{tr("x.car_price_omr")}</label>
                <input type="range" min="3000" max="80000" step="500" value={price} onChange={(e) => setPrice(+e.target.value)} data-testid="fin-page-price" />
                <div className="fin-val">OMR {price.toLocaleString()}</div>
              </div>
              <div className="fin-row"><label>{tr("x.down_payment")}{downPct}%)</label>
                <input type="range" min="0" max="60" step="5" value={downPct} onChange={(e) => setDownPct(+e.target.value)} data-testid="fin-page-down" />
                <div className="fin-val">OMR {Math.round(price * downPct / 100).toLocaleString()}</div>
              </div>
              <div className="fin-row"><label>{tr("x.term")}</label>
                <div className="fin-terms">
                  {terms.map((tm) => (<button key={tm} type="button" className={term === tm ? "active" : ""} aria-pressed={term === tm} onClick={() => setTerm(tm)} data-testid={`fin-page-term-${tm}`}>{tr("px.months_short", { n: tm })}</button>))}
                </div>
              </div>
            </div>
            <div className="fin-result">
              <div className="kicker on-sec">{tr("x.estimated_monthly")}</div>
              <div className="fin-monthly">OMR <AnimatedAmount value={monthly} /></div>
              <div className="fin-meta">{tr("x.at")} {rate}{tr("x.flat")} {term} {tr("x.months")}</div>
              <a href="#finance-apply" className="pub-cta primary big" style={{ marginTop: 20 }} data-testid="fin-page-apply">{tr("x.apply_for_finance")}</a>
              <div className="fin-disclaimer">{disclaimer}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="finance-apply" style={{ padding: "48px 0 120px", background: "var(--pub-surface)" }}>
        <div className="public-container" style={{ maxWidth: 720 }}>
          <h2 style={{ fontSize: 32, fontWeight: 400, letterSpacing: "-0.02em", margin: "0 0 24px" }}>{tr("x.apply_for_finance")}</h2>
          <form className="enquiry-form" onSubmit={submit} data-testid="finance-apply-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" style={{ display: "none" }} data-testid="finance-honeypot" />
            <input placeholder={tr("x.your_name")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="finance-name" />
            <input placeholder={tr("x.mobile_2")} required inputMode="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="finance-mobile" />
            <input placeholder={tr("x.email_optional")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="finance-email" />
            <textarea placeholder={tr("x.which_car_are_you_interested_in")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="finance-message" />
            <button type="submit" disabled={sending} data-testid="finance-submit">{sending ? tr("x.sending") : tr("x.apply_for_finance")}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
